import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from './firebase';
import { MapPin, Plus, Edit3, Trash2, X, Building2, QrCode, Printer, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import LoadingSpinner from './components/LoadingSpinner';
import { filterGroupsByAccess } from './utils/locationAccess';

export default function GroupsManager({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [qrGroup, setQrGroup] = useState(null);

  useEffect(() => {
    if (!companyId) return;

    const unsub = onSnapshot(
      collection(db, 'companies', companyId, 'shiftsGroups'),
      (snapshot) => {
        setGroups(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to groups:', error);
        setLoading(false);
      }
    );

    return unsub;
  }, [companyId]);

  const saveGroup = async (groupData) => {
    try {
      if (editingGroup) {
        await updateDoc(doc(db, 'companies', companyId, 'shiftsGroups', editingGroup.id), {
          ...groupData,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, 'companies', companyId, 'shiftsGroups'), {
          ...groupData,
          companyId,
          createdBy: user.uid,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      setShowModal(false);
      setEditingGroup(null);
    } catch (error) {
      console.error('Error saving group:', error);
      alert(t('groups.saveFailed'));
    }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(t('groups.confirmDelete', { name: groupName }))) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'shiftsGroups', groupId));
    } catch (error) {
      console.error('Error deleting group:', error);
      alert(t('groups.deleteFailed'));
    }
  };

  const visibleGroups = filterGroupsByAccess(groups, userData);

  const filteredGroups = visibleGroups.filter(g => {
    const name = (g.name || '').toLowerCase();
    const address = (g.address || '').toLowerCase();
    return name.includes(searchQuery.toLowerCase()) || address.includes(searchQuery.toLowerCase());
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 size={24} className="text-primary" />
              {t('groups.title')}
            </h1>
            <p className="text-gray-400 mt-1">{t('groups.subtitle', { count: visibleGroups.length })}</p>
          </div>
          <button
            onClick={() => { setEditingGroup(null); setShowModal(true); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            <Plus size={16} /> {t('groups.addNew')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('groups.searchPlaceholder')}
          className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-primary/50 focus:border-primary"
        />
      </div>

      {/* Groups list */}
      {filteredGroups.length === 0 ? (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <Building2 size={48} className="text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">{t('groups.noGroups')}</p>
          <p className="text-sm text-gray-500 mt-1">{t('groups.noGroupsHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGroups.map(group => (
            <div key={group.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: group.color || '#2563EB' }}
                  >
                    {(group.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{group.name}</p>
                    {group.address && <p className="text-sm text-gray-400">{group.address}</p>}
                  </div>
                </div>
              </div>

              {group.description && (
                <p className="mt-3 text-sm text-gray-400">{group.description}</p>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setQrGroup(group)}
                  className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-green-400 border border-green-400/30 rounded-lg hover:bg-green-400/5"
                  title={t('groups.showQR')}
                >
                  <QrCode size={14} />
                </button>
                <button
                  onClick={() => { setEditingGroup(group); setShowModal(true); }}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-primary border border-primary/30 rounded-lg hover:bg-primary/5"
                >
                  <Edit3 size={14} /> {t('common.edit')}
                </button>
                <button
                  onClick={() => deleteGroup(group.id, group.name)}
                  className="flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-red-400 border border-red-400/30 rounded-lg hover:bg-red-400/5"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Group Modal */}
      {showModal && (
        <GroupModal
          group={editingGroup}
          onClose={() => { setShowModal(false); setEditingGroup(null); }}
          onSave={saveGroup}
        />
      )}

      {/* QR Code Modal */}
      {qrGroup && (
        <QRCodeModal
          group={qrGroup}
          onClose={() => setQrGroup(null)}
        />
      )}
    </div>
  );
}

const COLOR_OPTIONS = [
  '#2563EB', '#059669', '#D97706', '#DC2626', '#7C3AED',
  '#DB2777', '#0891B2', '#4F46E5', '#16A34A', '#EA580C',
];

function GroupModal({ group, onClose, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: group?.name || '',
    address: group?.address || '',
    description: group?.description || '',
    color: group?.color || '#2563EB',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave({
      name: formData.name.trim(),
      address: formData.address.trim(),
      description: formData.description.trim(),
      color: formData.color,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-white/10 rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            {group ? t('groups.edit') : t('groups.addNew')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-gray-300">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('groups.name')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
              placeholder={t('groups.namePlaceholder')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('groups.address')}</label>
            <input
              type="text"
              value={formData.address}
              onChange={e => setFormData({ ...formData, address: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
              placeholder={t('groups.addressPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('groups.description')}</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white"
              rows={2}
              placeholder={t('groups.descriptionPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('groups.color')}</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {COLOR_OPTIONS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFormData({ ...formData, color: c })}
                  className={`w-8 h-8 rounded-full border-2 transition-transform ${
                    formData.color === c ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" className="flex-1 bg-primary text-white rounded-lg py-2 font-medium hover:bg-primary-dark transition-colors">
              {t('common.save')}
            </button>
            <button type="button" onClick={onClose} className="flex-1 bg-white/10 text-gray-300 rounded-lg py-2 font-medium hover:bg-white/20 transition-colors">
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QRCodeModal({ group, onClose }) {
  const { t } = useTranslation();
  const qrRef = useRef(null);

  const clockInUrl = `${window.location.origin}/timeclock?group=${group.id}`;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const svgEl = qrRef.current?.querySelector('svg');
    const svgData = svgEl ? new XMLSerializer().serializeToString(svgEl) : '';
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head><title>${group.name} — Clock-In QR</title>
      <style>
        body { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; font-family:system-ui,sans-serif; margin:0; }
        h1 { font-size:24px; margin-bottom:4px; }
        p { color:#666; margin-top:0; font-size:14px; }
        .qr { margin:20px 0; }
        .url { font-size:11px; color:#999; word-break:break-all; max-width:300px; text-align:center; }
      </style>
      </head>
      <body>
        <h1>${group.name}</h1>
        ${group.address ? `<p>${group.address}</p>` : ''}
        <p>${t('timeClock.scanToClockIn')}</p>
        <div class="qr">${svgData}</div>
        <p class="url">${clockInUrl}</p>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  const handleDownload = () => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const img = new Image();
    img.onload = () => {
      canvas.width = 400;
      canvas.height = 400;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 400);
      ctx.drawImage(img, 0, 0, 400, 400);
      const link = document.createElement('a');
      link.download = `${group.name.replace(/\s+/g, '-')}-qr.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-white/10 rounded-xl shadow-xl max-w-sm w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <QrCode size={20} className="text-primary" />
            {t('groups.qrCode')}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded text-gray-300">
            <X size={20} />
          </button>
        </div>

        <div className="text-center space-y-4">
          {/* Group info */}
          <div className="flex items-center justify-center gap-2">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: group.color || '#2563EB' }}
            >
              {(group.name || '?')[0].toUpperCase()}
            </div>
            <div className="text-left">
              <p className="font-semibold text-white">{group.name}</p>
              {group.address && <p className="text-xs text-gray-400">{group.address}</p>}
            </div>
          </div>

          {/* QR Code */}
          <div ref={qrRef} className="bg-white rounded-xl p-4 inline-block">
            <QRCodeSVG
              value={clockInUrl}
              size={220}
              level="H"
              includeMargin={false}
            />
          </div>

          <p className="text-sm text-gray-400">{t('timeClock.scanToClockIn')}</p>
          <p className="text-xs text-gray-500 break-all">{clockInUrl}</p>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={handlePrint}
              className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-white rounded-lg py-2.5 font-medium hover:bg-primary-dark transition-colors text-sm"
            >
              <Printer size={16} /> {t('groups.printQR')}
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-1.5 bg-white/10 text-gray-300 rounded-lg py-2.5 font-medium hover:bg-white/20 transition-colors text-sm"
            >
              <Download size={16} /> {t('groups.downloadQR')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
